import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { storeApi } from "@/lib/storefront";

const WISHLIST_KEY = "store_wishlist_v1";
const PRUNE_THROTTLE_MS = 30_000;
const WishlistContext = createContext(null);

function read() {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function write(v) {
  try { localStorage.setItem(WISHLIST_KEY, JSON.stringify(v)); } catch (e) { console.warn(e); }
}

export function WishlistProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { return typeof window !== "undefined" ? read() : []; } catch { return []; }
  });
  const hydrated = useRef(true);
  const lastPruneRef = useRef(0);
  const location = useLocation();

  useEffect(() => {
    if (!hydrated.current) return;
    write(items);
  }, [items]);

  // Drop wishlist entries whose products were deleted or hidden in admin.
  const prune = useCallback(async (force = false) => {
    const now = Date.now();
    if (!force && now - lastPruneRef.current < PRUNE_THROTTLE_MS) return;
    const current = read();
    if (current.length === 0) return;
    try {
      const ids = current.map((p) => p.id).filter(Boolean);
      const { data } = await storeApi.post("/products/check-alive", { ids });
      const alive = new Set(data?.alive || []);
      const pruned = current.filter((p) => alive.has(p.id));
      lastPruneRef.current = now;
      if (pruned.length !== current.length) {
        setItems(pruned);
      }
    } catch {
      // Network hiccup — keep what we have
    }
  }, []);

  useEffect(() => { prune(true); }, [prune]);
  useEffect(() => { prune(); }, [location.pathname, prune]);
  useEffect(() => {
    const onFocus = () => prune();
    const onVisible = () => { if (document.visibilityState === "visible") prune(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [prune]);

  const toggle = useCallback((product) => {
    setItems((prev) => {
      if (prev.some((x) => x.id === product.id)) return prev.filter((x) => x.id !== product.id);
      return [
        {
          id: product.id,
          title: product.title,
          image: product.primary_image || "",
          price: Number(product.price || 0),
          currency: product.currency || "USD",
          seo_slug: product.seo_slug || product.id,
        },
        ...prev,
      ];
    });
  }, []);

  const has = useCallback((productId) => items.some((x) => x.id === productId), [items]);
  const remove = useCallback((productId) => {
    setItems((prev) => prev.filter((x) => x.id !== productId));
  }, []);

  return (
    <WishlistContext.Provider value={{ items, toggle, has, remove, count: items.length }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error("useWishlist must be used within WishlistProvider");
  return ctx;
}
